import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import service from '../../Service/Service';

const PageDealsSection = () => {
  const [deals, setDeals] = useState([]);
  const [umrahPackages, setUmrahPackages] = useState([]);
  const [holidayPackages, setHolidayPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      try {
        // Fetch flight page deals, Umrah packages, and Holiday packages in parallel
        // Note: Both Umrah and Holiday packages are stored in packages_umrah table
        const [dealsResponse, packagesResponse] = await Promise.all([
          service.getPageDeals().catch(err => {
            console.error('Failed to fetch page deals:', err);
            return null;
          }),
          service.getAllpackageUmrah().catch(err => {
            console.error('Failed to fetch packages for page display:', err);
            return { data: [] };
          }),
        ]);

        // Flight deals (unchanged)
        if (
          dealsResponse &&
          dealsResponse.data &&
          dealsResponse.data.success &&
          dealsResponse.data.deals &&
          dealsResponse.data.deals.length > 0
        ) {
          setDeals(dealsResponse.data.deals);
          console.log(`✅ Loaded ${dealsResponse.data.deals.length} page deals`);
        } else {
          console.log(`ℹ️ No page deals found`);
          setDeals([]);
        }

        // Umrah packages that should show "as page"
        const allPackages = packagesResponse?.data || [];
        const pageUmrah = allPackages.filter((pkg) =>
          pkg.package_type === 'Umrah' &&
          (pkg.show_as_page === true || pkg.show_as_page === 1 || pkg.show_as_page === '1')
        );
        console.log(`✅ Loaded ${pageUmrah.length} Umrah page packages`);
        setUmrahPackages(pageUmrah);

        // Holiday packages that should show "as page"
        // Holiday packages are also stored in packages_umrah table with package_type === "Holiday"
        const pageHoliday = allPackages.filter((pkg) =>
          pkg.package_type === 'Holiday' &&
          (pkg.show_as_page === true || pkg.show_as_page === 1 || pkg.show_as_page === '1')
        );
        console.log(`✅ Loaded ${pageHoliday.length} Holiday page packages`);
        setHolidayPackages(pageHoliday);
      } catch (error) {
        console.error('Failed to fetch page data:', error);
        setDeals([]);
        setUmrahPackages([]);
        setHolidayPackages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, []);

  const handleDealClick = (deal) => {
    // If deal has a seo_slug, navigate to the deal landing page
    if (deal.seo_slug) {
      console.log('🎯 Navigating to deal landing page:', deal.seo_slug);
      navigate(`/deals/${deal.seo_slug}`);
      return;
    }
    
    // Fallback: If no seo_slug, use the old behavior (navigate to flight search)
    // Prepare booking data from deal
    const bookingData = {
      departure: deal.origin_code,
      arrival: deal.destination_code,
      departure_name: `${deal.origin_city} (${deal.origin_code})`,
      arrival_name: `${deal.destination_city} (${deal.destination_code})`,
      date: deal.departure_date || '',
      return_date: deal.return_date || '',
      adults: '1',
      children: '0',
      infants: '0',
      flight_type: deal.return_date ? 'return' : 'one-way',
      flight_class: 'economy'
    };

    // Store booking data in localStorage
    localStorage.setItem('booking', JSON.stringify(bookingData));
    localStorage.removeItem('offer'); // Clear cached offers to force fresh search

    // Navigate to flight details page
    navigate('/flight-detail-search?package=0');
  };

  // Hide section if nothing to show
  if (!loading && deals.length === 0 && umrahPackages.length === 0 && holidayPackages.length === 0) {
    return null;
  }

  return (
    <div className="mt-12 mb-6 w-[90%] md:w-[80%] mx-auto space-y-6">
      {/* Popular Cheap Flight Routes */}
      {(loading && (deals.length > 0 || umrahPackages.length > 0)) || loading ? (
        <div className="flex justify-center items-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
        </div>
      ) : (
        <>
          {deals.length > 0 && (
            <section>
              <div className="mb-4">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                  Popular Cheap Flight Routes
                </h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {deals.map((deal, index) => (
                  <button
                    key={deal.id || index}
                    type="button"
                    onClick={() => handleDealClick(deal)}
                    className="px-4 py-3 rounded-md font-medium text-sm text-white bg-secondary hover:bg-navy-700 transition-colors"
                  >
                    Cheap flights from {deal.origin_city} to {deal.destination_city}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Separate section for Umrah packages with similar design */}
          {umrahPackages.length > 0 && (
            <section>
              <div className="mb-4">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                  Popular Umrah Packages
                </h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {umrahPackages.map((pkg) => (
                  <button
                    key={pkg.uid}
                    type="button"
                    onClick={() => navigate(`/packagedetail?package=${pkg.uid}`)}
                    className="px-4 py-3 rounded-md font-medium text-sm text-white bg-secondary hover:bg-navy-700 transition-colors"
                  >
                    {pkg.name || 'View Umrah package details'}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Separate section for Holiday packages with similar design */}
          {holidayPackages.length > 0 && (
            <section>
              <div className="mb-4">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                  Popular Holiday Packages
                </h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {holidayPackages.map((pkg) => (
                  <button
                    key={pkg.uid}
                    type="button"
                    onClick={() => navigate(`/packagedetail?package=${pkg.uid}`)}
                    className="px-4 py-3 rounded-md font-medium text-sm text-white bg-secondary hover:bg-navy-700 transition-colors"
                  >
                    {pkg.name || 'View holiday package details'}
                  </button>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default PageDealsSection;
